/**
 * Zod schemas + DTO types for the Clients API surface — Phase 4 W3.T3.2.
 *
 * Source-of-truth: `.planning/phases/04-projects-clients/CONTEXT.md` (Q06,
 * Q08, Q09, Q23, Q41) + `.planning/phases/04-projects-clients/PLAN.md`
 * §"Wave 3" + §"S-9" + §"M-4".
 *
 * IMPORTANT (MF-1 fix):
 *   - The field set below is VERBATIM-LOCKED to CONTEXT/PLAN.
 *     `research-frontend.md §6.2` is HISTORICAL — do not consult it.
 *   - Q06 = b: Client carries
 *     `name + inn(10|12, optional, UNIQUE WHERE NOT NULL)
 *     + contactPersonName + phone + email + notes`.
 *     NO `kpp`, NO `address`, NO `ogrn`, NO `billingEmail`, NO `paymentTerms`.
 *   - Q06 deploy split: Phase 4 ships migration 013a (add `notes` + backfill
 *     from `contactInfo`); 013b (DROP `contactInfo`) lands in Phase 5/6.
 *     Schemas here NEVER read or write `contactInfo`.
 *   - Q41: pagination is offset-based — `?limit&offset`. NO `?page`.
 *   - INN: 10 or 12 digits. Optional whitespace/dash separators are
 *     TRANSFORMED OUT before validation (W-4) — so `"7707 083893"` and
 *     `"7707-083893"` both normalise to `"7707083893"`.
 */
import { z } from 'zod';
import { paginatedSchema } from './projects';
import { ruEnum } from './lib/zod-ru';

// ─── Shared primitives ────────────────────────────────────────────────────

const UuidSchema = z.string().uuid();
const IsoDateTimeSchema = z.string().datetime({ offset: true });

/**
 * Разделители, допустимые ВНУТРИ ИНН и снимаемые перед проверкой: пробелы
 * (включая неразрывный — `\s` его покрывает), дефис-минус и типографские
 * тире U+2010..U+2015 / U+2212. Последние приезжают при копировании из PDF
 * и договоров (FT.D8 / UIDA-S6).
 *
 * Класс намеренно УЗКИЙ. Прежняя нормализация на форме снимала всё
 * нецифровое (`\D`), из-за чего строка из десяти букв превращалась в
 * пустую и проходила как «ИНН не указан».
 */
export const INN_SEPARATOR_REGEX = /[\s\u2010-\u2015\u2212-]/g;

/** Единый текст ошибки формата ИНН — один на клиент и сервер. */
export const INN_FORMAT_MESSAGE = 'ИНН должен содержать 10 или 12 цифр';

/**
 * Канонический вид ИНН: снять внешние пробелы и внутренние разделители.
 * Всё, что не цифра и не разделитель, СОХРАНЯЕТСЯ — иначе проверка ниже
 * не увидит мусор и молча его выбросит.
 */
export function normaliseInn(raw: string): string {
  return raw.trim().replace(INN_SEPARATOR_REGEX, '');
}

/** Канонический ИНН — ровно 10 или 12 цифр. */
export function isCanonicalInn(value: string): boolean {
  return /^\d{10}$|^\d{12}$/.test(value);
}

/**
 * INN (Russian taxpayer ID). Accepts 10 or 12 digits, with optional
 * spaces/dashes that get stripped before format validation. Empty string
 * is treated as "no INN" and normalised to `null` so the wire shape stays
 * `string | null` — callers can clear the field without sending a separate
 * `null` literal.
 *
 * Mirrors `apps/api/src/clients/clients.controller.ts` (W-4) — обе схемы
 * используют одни и те же `normaliseInn` / `isCanonicalInn`.
 */
const InnSchema = z
  .string()
  .transform(normaliseInn)
  .refine((s) => s.length === 0 || isCanonicalInn(s), { message: INN_FORMAT_MESSAGE })
  .transform((s) => (s.length === 0 ? null : s))
  .nullable()
  .optional();

/**
 * Email — trimmed, lowercased, then validated. Stricter `max(254)` mirrors
 * RFC 5321 path length so we never overflow downstream LDAP/SMTP layers.
 */
const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .email('Введите корректный email')
  .nullable()
  .optional();

/**
 * Phone — lenient by design. We accept digits + spaces + dashes + parens +
 * leading `+`; max 40 chars; everything else rejected. Formatting/parsing
 * is a Phase 6 concern (NumPad component → libphonenumber-js).
 */
const PhoneSchema = z
  .string()
  .trim()
  .max(40, { message: 'Телефон содержит недопустимые символы или превышает 40 знаков' })
  .regex(/^[+\d()\-\s]*$/, {
    message: 'Телефон содержит недопустимые символы или превышает 40 знаков',
  })
  .nullable()
  .optional();

const ContactPersonNameSchema = z.string().trim().max(200).nullable().optional();
const NotesSchema = z.string().max(2000).nullable().optional();

// ─── List query ───────────────────────────────────────────────────────────

/**
 * `GET /clients` query schema. Same offset/limit/search/status pattern
 * as `/projects`, but the `status` enum here is lowercased
 * `'active' | 'archived'` per the controller convention (Q23 — there's no
 * `'COMPLETED'` lifecycle on clients).
 */
export const ListClientsQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(100).default(25),
    offset: z.coerce.number().int().min(0).max(5000).default(0),
    status: z.enum(['active', 'archived'], ruEnum('Недопустимый статус')).optional(),
    search: z.string().trim().min(1).max(200).optional(),
  })
  .strict();
export type ListClientsQuery = z.infer<typeof ListClientsQuerySchema>;

// ─── Read shapes ──────────────────────────────────────────────────────────

/**
 * Compact list row returned by `GET /clients`. Q06=b VERBATIM-LOCKED.
 * NO `kpp`. NO `address`. NO `totalBudget`. `notes` is detail-only — list
 * rows omit it to keep responses lean.
 */
export const ClientListItemSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  inn: z.string().nullable(),
  contactPersonName: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  archivedAt: IsoDateTimeSchema.nullable(),
  totalProjects: z.number().int().nonnegative(),
  activeProjects: z.number().int().nonnegative(),
  version: z.number().int().nonnegative(),
});
export type ClientListItem = z.infer<typeof ClientListItemSchema>;

/** Alias kept for parity with `ProjectSummarySchema` naming. */
export const ClientSummarySchema = ClientListItemSchema;
export type ClientSummary = z.infer<typeof ClientSummarySchema>;

/**
 * Full client shape — list row + `notes` + audit timestamps. Returned by
 * `GET /clients/:id` (and used by the FE edit modal).
 */
export const ClientSchema = ClientListItemSchema.extend({
  notes: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Client = z.infer<typeof ClientSchema>;

/** `GET /clients` envelope. */
export const ClientListResponseSchema = paginatedSchema(ClientListItemSchema);
export type ClientListResponse = z.infer<typeof ClientListResponseSchema>;

// ─── Write requests ───────────────────────────────────────────────────────

/**
 * `POST /clients` body — VERBATIM-LOCKED per Q06=b.
 * NO `kpp`. NO `address`. NO `contactInfo`.
 */
export const ClientCreateSchema = z
  .object({
    name: z.string().trim().min(2).max(200),
    inn: InnSchema,
    contactPersonName: ContactPersonNameSchema,
    phone: PhoneSchema,
    email: EmailSchema,
    notes: NotesSchema,
  })
  .strict();
export type ClientCreate = z.infer<typeof ClientCreateSchema>;

/**
 * `PATCH /clients/:id` body. `version` REQUIRED for optimistic-lock
 * (T2.3, FI-3 lock-extension allow-list).
 */
export const ClientUpdateSchema = z
  .object({
    version: z.number().int().nonnegative(),
    name: z.string().trim().min(2).max(200).optional(),
    inn: InnSchema,
    contactPersonName: ContactPersonNameSchema,
    phone: PhoneSchema,
    email: EmailSchema,
    notes: NotesSchema,
  })
  .strict();
export type ClientUpdate = z.infer<typeof ClientUpdateSchema>;

/**
 * `POST /clients/:id/archive` + `/restore` body. `version` REQUIRED.
 *
 * Q08: archive is rejected at 422 if any of the client's projects are
 * still active (service layer; no schema-level enforcement possible).
 */
export const ArchiveClientSchema = z
  .object({
    version: z.number().int().nonnegative(),
  })
  .strict();
export type ArchiveClient = z.infer<typeof ArchiveClientSchema>;

/** Restore body — same shape as archive (lock check + audit only). */
export const RestoreClientSchema = ArchiveClientSchema;
export type RestoreClient = z.infer<typeof RestoreClientSchema>;
